import { createSwrKeyedStore } from './swr/createSwrKeyedStore';
import { createSwrListStore } from './swr/createSwrListStore';
import { createSwrSingletonStore } from './swr/createSwrSingletonStore';
import {
  getWorkflow,
  getWorkflowNodeTypes,
  getWorkflowRun,
  listWorkflowApprovals,
  listWorkflowRuns,
  listWorkflows,
} from '../lib/api';

const workflows = createSwrListStore({
  name: 'WorkflowsStore',
  storageKey: 'bookthing-workflows',
  itemsField: 'workflows',
  setters: {
    set: 'setWorkflows',
    add: 'addWorkflow',
    update: 'updateWorkflow',
    remove: 'removeWorkflow',
    reset: 'resetWorkflows',
  },
  fetcher: async () => {
    const response = await listWorkflows();
    return response.items || [];
  },
});

const workflowDetails = createSwrKeyedStore({
  name: 'WorkflowDetailsStore',
  storageKey: 'bookthing-workflow-details',
  fetcher: getWorkflow,
});

const workflowRuns = createSwrKeyedStore({
  name: 'WorkflowRunsStore',
  storageKey: 'bookthing-workflow-runs',
  fetcher: async (workflowId) => {
    const response = await listWorkflowRuns(workflowId);
    return response.items || [];
  },
});

const workflowRunDetails = createSwrKeyedStore({
  name: 'WorkflowRunDetailsStore',
  storageKey: 'bookthing-workflow-run-details',
  fetcher: getWorkflowRun,
});

const workflowApprovals = createSwrListStore({
  name: 'WorkflowApprovalsStore',
  storageKey: 'bookthing-workflow-approvals',
  itemsField: 'approvals',
  setters: {
    set: 'setApprovals',
    add: 'addApproval',
    update: 'updateApproval',
    remove: 'removeApproval',
    reset: 'resetApprovals',
  },
  fetcher: async () => {
    const response = await listWorkflowApprovals('pending');
    return response.items || [];
  },
});

const workflowNodeTypes = createSwrSingletonStore({
  name: 'WorkflowNodeTypesStore',
  storageKey: 'bookthing-workflow-node-types',
  fetcher: getWorkflowNodeTypes,
});

export const useWorkflowsStore = workflows.useStore;
export const useWorkflowsData = workflows.useData;
export const fetchWorkflowsIfStale = workflows.fetchIfStale;

export const useWorkflowDetailsStore = workflowDetails.useStore;
export const useWorkflowDetail = workflowDetails.useEntry;
export const fetchWorkflowDetailIfStale = workflowDetails.fetchIfStale;

export const useWorkflowRunsStore = workflowRuns.useStore;
export const useWorkflowRuns = workflowRuns.useEntry;
export const fetchWorkflowRunsIfStale = workflowRuns.fetchIfStale;

export const useWorkflowRunDetailsStore = workflowRunDetails.useStore;
export const useWorkflowRun = workflowRunDetails.useEntry;
export const fetchWorkflowRunIfStale = workflowRunDetails.fetchIfStale;

export const useWorkflowApprovalsStore = workflowApprovals.useStore;
export const useWorkflowApprovalsData = workflowApprovals.useData;
export const fetchWorkflowApprovalsIfStale = workflowApprovals.fetchIfStale;

export const useWorkflowNodeTypesStore = workflowNodeTypes.useStore;
export const useWorkflowNodeTypes = workflowNodeTypes.useData;
export const fetchWorkflowNodeTypesIfStale = workflowNodeTypes.fetchIfStale;

/**
 * Keep list/detail caches coherent after a workflow mutation. The owner is set
 * at the same time so a newly-created cache can never be read by another user.
 */
export function cacheWorkflowRecord(workflow, userId = null) {
  if (!workflow?.id) return;
  let list = workflows.useStore.getState();
  if (userId && list.cachedForUserId && list.cachedForUserId !== userId) {
    list.resetWorkflows();
    list = workflows.useStore.getState();
  }
  if (list.workflows.some((item) => item.id === workflow.id)) {
    list.updateWorkflow(workflow.id, workflow);
  } else {
    list.addWorkflow(workflow);
  }
  list.setHasFetched(true);
  if (userId) list.setCachedForUserId(userId);

  let details = workflowDetails.useStore.getState();
  if (userId && details.cachedForUserId && details.cachedForUserId !== userId) {
    details.reset();
    details = workflowDetails.useStore.getState();
  }
  details.setEntry(workflow.id, workflow);
  if (userId) details.setCachedForUserId(userId);
}

/**
 * Cache a newly-created or updated run and place it at the front of its
 * workflow's history without introducing duplicates.
 */
export function cacheWorkflowRunRecord(run, userId = null, workflowId = null) {
  if (!run?.id) return;
  let details = workflowRunDetails.useStore.getState();
  if (userId && details.cachedForUserId && details.cachedForUserId !== userId) {
    details.reset();
    details = workflowRunDetails.useStore.getState();
  }
  details.setEntry(run.id, run);
  if (userId) details.setCachedForUserId(userId);

  const ownerWorkflowId = workflowId || run.workflow_id;
  if (!ownerWorkflowId) return;
  let histories = workflowRuns.useStore.getState();
  if (userId && histories.cachedForUserId && histories.cachedForUserId !== userId) {
    histories.reset();
    histories = workflowRuns.useStore.getState();
  }
  const cached = histories.entries[ownerWorkflowId]?.data || [];
  histories.setEntry(ownerWorkflowId, [run, ...cached.filter((item) => item.id !== run.id)]);
  if (userId) histories.setCachedForUserId(userId);
}
